import { useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '../store/useStore.js';
import { CATEGORY_MAP, NEON_PALETTE, UNIQUE_ICONS, type CategoryKey } from '../utils/constants.js';
import { X, Upload, Loader2 } from 'lucide-react';
import { useCompletion } from '@ai-sdk/react';
import { api, ApiError } from '../lib/api.js';
import { AIConsentModal } from './AIConsentModal.js';
import { MicButton } from './MicButton.js';

function Icon({ name, size = 18 }: { name: string; size?: number }) {
  return <span className="material-symbols-outlined" style={{ fontSize: size }}>{name}</span>;
}

function SizeSlider({ value, color, onChange }: { value: number; color: string; onChange: (v: number) => void }) {
  const min = 10, max = 120;
  const pct = ((value - min) / (max - min)) * 100;
  const trackRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  function update(e: PointerEvent | React.PointerEvent) {
    const r = trackRef.current!.getBoundingClientRect();
    const p = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
    onChange(Math.round(min + p * (max - min)));
  }

  function onDown(e: React.PointerEvent) {
    dragging.current = true;
    update(e);
    const move = (ev: PointerEvent) => { if (dragging.current) update(ev); };
    const up = () => {
      dragging.current = false;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  return (
    <div className="syn-size" style={{ '--c': color } as React.CSSProperties} onPointerDown={onDown}>
      <div className="syn-size-track" ref={trackRef}>
        <div className="syn-size-fill" style={{ width: pct + '%' }} />
      </div>
      <div className="syn-size-thumb" style={{ left: pct + '%' }} />
      <div className="syn-size-ticks">
        <span>10</span><span>40</span><span>70</span><span>100</span><span>120</span>
      </div>
    </div>
  );
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function EditPanel() {
  const selectedId = useStore((s) => s.selectedId);
  const nodes = useStore((s) => s.nodes);
  const links = useStore((s) => s.links);
  const setSelected = useStore((s) => s.setSelected);
  const updateNode = useStore((s) => s.updateNode);
  const patchNode = useStore((s) => s.patchNode);
  const deleteNode = useStore((s) => s.deleteNode);
  const removeLink = useStore((s) => s.removeLink);
  const duplicateNode = useStore((s) => s.duplicateNode);
  const toggleNodeLock = useStore((s) => s.toggleNodeLock);
  const enterUniverse = useStore((s) => s.enterUniverse);
  const workspaceId = useStore((s) => s.workspaceId);

  const nd = nodes.find((n) => n.id === selectedId);
  const [localLabel, setLocalLabel] = useState('');
  const [iconQ, setIconQ] = useState('');
  const [iconMenuOpen, setIconMenuOpen] = useState(false);
  const [showAiConsent, setShowAiConsent] = useState(false);
  const [autoLinkCandidates, setAutoLinkCandidates] = useState<{documentId: string; title: string; similarity: number}[] | null>(null);
  const [isLinking, setIsLinking] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastIdRef = useRef<string | null>(null);

  const [slashMenu, setSlashMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    query: string;
    index: number;
  }>({
    visible: false,
    x: 0,
    y: 0,
    query: '',
    index: 0
  });

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const textTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Autosave do conteúdo enquanto digita (sem depender do blur)
  const pendingSaveRef = useRef<{ id: string; value: string } | null>(null);
  const saveTimerRef = useRef<number | null>(null);

  function flushContentSave() {
    if (saveTimerRef.current !== null) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    const p = pendingSaveRef.current;
    pendingSaveRef.current = null;
    if (!p) return;
    const cur = useStore.getState().nodes.find((n) => n.id === p.id);
    if (cur && (cur.content ?? '') !== p.value) updateNode(p.id, { content: p.value });
  }

  function queueContentSave(id: string, value: string) {
    pendingSaveRef.current = { id, value };
    if (saveTimerRef.current !== null) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(flushContentSave, 800);
  }

  // Garante persistência se o painel desmontar com save pendente
  useEffect(() => flushContentSave, []);

  function insertTextAtCursor(textToInsert: string, removeSlash = false) {
    const textarea = textareaRef.current;
    if (!textarea || !nd) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;
    
    let newValue = value;
    let newCursorPos = start + textToInsert.length;
    
    if (removeSlash && start > 0) {
      const slashIndex = value.lastIndexOf('/', start - 1);
      if (slashIndex !== -1) {
        newValue = value.slice(0, slashIndex) + textToInsert + value.slice(end);
        newCursorPos = slashIndex + textToInsert.length;
      }
    } else {
      newValue = value.slice(0, start) + textToInsert + value.slice(end);
    }
    
    textarea.value = newValue;
    textarea.focus();
    textarea.setSelectionRange(newCursorPos, newCursorPos);
    
    updateNode(nd.id, { content: newValue });
    setSlashMenu(prev => ({ ...prev, visible: false }));
  }

  const slashItems = [
    { label: 'Data Atual', desc: 'Insere a data de hoje', icon: 'calendar_month', color: '#ffcc00', action: () => insertTextAtCursor(' [' + new Date().toLocaleDateString('pt-BR') + '] ', true) },
    { label: 'Checkbox', desc: 'Cria uma lista de afazeres', icon: 'check_box', color: '#00ff88', action: () => insertTextAtCursor('\n[ ] ', true) },
    { label: 'Marcador / Destaque', desc: 'Destaca o texto', icon: 'border_color', color: '#00f5ff', action: () => insertTextAtCursor('==texto destacado==', true) },
    { label: 'Título Principal', desc: 'Título grande H1', icon: 'format_h1', color: '#bf00ff', action: () => insertTextAtCursor('\n# ', true) },
    { label: 'Subtítulo', desc: 'Título médio H2', icon: 'format_h2', color: '#ff007f', action: () => insertTextAtCursor('\n## ', true) },
  ];

  const getFilteredSlashItems = () => {
    if (!slashMenu.query) return slashItems;
    return slashItems.filter(item => 
      item.label.toLowerCase().includes(slashMenu.query.toLowerCase()) ||
      item.desc.toLowerCase().includes(slashMenu.query.toLowerCase())
    );
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target;
    const { selectionStart, value } = textarea;

    if (nd) queueContentSave(nd.id, value);

    const textBeforeCaret = value.slice(0, selectionStart);
    const lastChar = textBeforeCaret.slice(-1);
    
    if (lastChar === '/') {
      const lines = textBeforeCaret.split('\n');
      const currentLine = lines.length;
      const currentChar = lines[lines.length - 1]?.length ?? 0;
      
      setSlashMenu({
        visible: true,
        x: Math.min(textarea.clientWidth - 240, Math.max(20, currentChar * 8 + 20)),
        y: Math.min(textarea.clientHeight - 180, currentLine * 22 + 20),
        query: '',
        index: 0
      });
    } else if (slashMenu.visible) {
      const match = textBeforeCaret.match(/\/(\w*)$/);
      if (match) {
        setSlashMenu(prev => ({ ...prev, query: match[1] || '' }));
      } else {
        setSlashMenu(prev => ({ ...prev, visible: false }));
      }
    }
  };

  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!slashMenu.visible) return;
    
    const menuItems = getFilteredSlashItems();
    if (menuItems.length === 0) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSlashMenu(prev => ({ ...prev, index: (prev.index + 1) % menuItems.length }));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSlashMenu(prev => ({ ...prev, index: (prev.index - 1 + menuItems.length) % menuItems.length }));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const selectedItem = menuItems[slashMenu.index];
      if (selectedItem) {
        selectedItem.action();
      }
      setSlashMenu(prev => ({ ...prev, visible: false }));
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setSlashMenu(prev => ({ ...prev, visible: false }));
    }
  };

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !workspaceId || !nd) return;
    setIsUploading(true);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('workspaceId', workspaceId);
      const res = await fetch(`/api/assets/upload-direct?workspaceId=${workspaceId}`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) throw new Error('Falha no upload');
      const data = await res.json();
      updateNode(nd.id, { content: data.assetId });
    } catch (err) {
      console.error(err);
      setUploadError('Erro ao enviar a imagem. Tente novamente.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  const { complete: suggestAi, completion: suggestion, isLoading: isSuggesting } = useCompletion({
    api: '/api/ai/suggest',
    body: { documentId: nd?.id },
    fetch: async (url, options) => {
      const b = JSON.parse(options?.body as string);
      if (b.prompt) b.context = b.prompt;
      delete b.prompt;
      return fetch(url, { ...options, body: JSON.stringify(b), credentials: 'include' });
    },
    onError: (err) => {
      if (err.message.includes('ai_consent_required') || err.message.includes('403')) {
        setShowAiConsent(true);
      } else {
        alert('Erro na IA: ' + err.message);
      }
    }
  });

  async function handleAutoLink() {
    if (!nd) return;
    setIsLinking(true);
    try {
      const res = await api<{ candidates: { documentId: string; title: string; similarity: number }[] }>('/ai/autolink', {
        method: 'POST',
        body: { documentId: nd.id }
      });
      setAutoLinkCandidates(res.candidates);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setShowAiConsent(true);
      } else {
        alert('Erro ao buscar links automáticos: ' + (err as Error).message);
      }
    } finally {
      setIsLinking(false);
    }
  }

  useEffect(() => {
    if (nd && nd.id !== lastIdRef.current) {
      setLocalLabel(nd.label || '');
      lastIdRef.current = nd.id;
    }
  }, [nd]);

  const linked = useMemo(() => {
    if (!nd) return [];
    return links
      .filter((l) => l.source === nd.id || l.target === nd.id)
      .map((l) => {
        const otherId = l.source === nd.id ? l.target : l.source;
        const other = nodes.find((n) => n.id === otherId);
        return other ? { other, link: l } : null;
      })
      .filter(Boolean) as Array<{ other: (typeof nodes)[number]; link: (typeof links)[number] }>;
  }, [nd, links, nodes]);

  const filteredIcons = iconQ
    ? UNIQUE_ICONS.filter((n) => n.includes(iconQ.toLowerCase()))
    : UNIQUE_ICONS;

  if (!nd) return null;

  const c = nd.color || '#00f5ff';
  const cat = CATEGORY_MAP[(nd.category as CategoryKey) ?? 'idea'];

  function commitLabel() {
    if (!nd) return;
    if (localLabel.trim() && localLabel !== nd.label) updateNode(nd.id, { label: localLabel.trim() });
  }

  return (
    <aside
      key={nd.id}
      className="syn-edit animate-fade-in"
      style={{ '--c': c } as React.CSSProperties}
    >
      <div className="syn-edit-hd">
        <div className="syn-edit-title-row">
          <span className="syn-edit-dot" />
          <input
            className="syn-edit-title"
            value={localLabel}
            onChange={(e) => setLocalLabel(e.target.value)}
            onBlur={commitLabel}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            placeholder="Sem título"
          />
          <button className="syn-edit-close" onClick={() => setSelected(null)} title="Fechar">
            <X size={14} />
          </button>
        </div>
        <div className="syn-edit-meta">
          <span>{nd.type === 'text' ? 'Texto' : nd.type === 'image' ? 'Imagem' : 'Tópico'}</span>
          <span className="dot-sep">·</span>
          <span>{linked.length} conexões</span>
          {nd.locked && <><span className="dot-sep">·</span><span style={{ color: 'var(--warn, #ffb800)' }}>travado</span></>}
        </div>
      </div>

      <div className="syn-edit-body">
        <div className="syn-section">
          <div className="syn-section-hd">
            <span className="syn-section-lbl">Tipo do Nó</span>
          </div>
          <div className="syn-seg">
            {[
              { key: 'idea', label: 'Tópico', color: '#bf00ff', icon: 'lightbulb' },
              { key: 'text', label: 'Texto', color: '#00ff88', icon: 'edit_note' },
              { key: 'image', label: 'Imagem', color: '#ffcc00', icon: 'image' }
            ].map((t) => {
              const isActive = (nd.type || 'idea') === t.key;
              return (
                <button
                  key={t.key}
                  className={isActive ? 'is-active' : ''}
                  style={(isActive ? { '--c': t.color } : {}) as React.CSSProperties}
                  onClick={() => updateNode(nd.id, { 
                    type: t.key as any, 
                    icon: nd.icon === 'lightbulb' || nd.icon === 'edit_note' || nd.icon === 'image' || !nd.icon ? t.icon : nd.icon,
                    color: nd.color === '#00f5ff' || nd.color === '#bf00ff' || nd.color === '#00ff88' || nd.color === '#ffcc00' || !nd.color ? t.color : nd.color
                  })}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="syn-section">
          <div className="syn-section-hd">
            <span className="syn-section-lbl">Cor</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {NEON_PALETTE.map((p) => (
              <button
                key={p}
                style={{
                  width: 22, height: 22, borderRadius: '50%', background: p, border: 'none', cursor: 'pointer',
                  boxShadow: c === p ? `0 0 0 2px var(--bg-card, #0a0a0f), 0 0 0 4px ${p}, 0 0 12px ${p}` : `0 0 4px ${p}`,
                  transition: 'all 0.2s ease',
                  opacity: c === p ? 1 : 0.4,
                  transform: c === p ? 'scale(1.1)' : 'scale(1)'
                }}
                onClick={() => updateNode(nd.id, { color: p })}
                title={p}
              />
            ))}
          </div>
        </div>

        <div className="syn-section">
          <div className="syn-section-hd">
            <span className="syn-section-lbl">Tamanho</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--muted)' }}>{nd.size}px</span>
          </div>
          <SizeSlider
            value={nd.size}
            color={c}
            onChange={(v) => {
              patchNode(nd.id, { size: v });
              updateNode(nd.id, { size: v });
            }}
          />
        </div>

        <div className="syn-section" style={{ position: 'relative' }}>
          <div className="syn-section-hd">
            <span className="syn-section-lbl">Ícone</span>
          </div>
          
          <button 
            onClick={() => setIconMenuOpen(!iconMenuOpen)}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, color: 'var(--fg)', cursor: 'pointer', width: '100%', justifyContent: 'space-between', transition: 'all 0.2s' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name={nd.icon || 'lightbulb'} size={18} />
              <span style={{ fontSize: 13, textTransform: 'capitalize' }}>{nd.icon || 'lightbulb'}</span>
            </div>
            <Icon name={iconMenuOpen ? "keyboard_arrow_up" : "keyboard_arrow_down"} size={16} />
          </button>

          {iconMenuOpen && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, padding: 12, background: 'rgba(10, 10, 15, 0.95)', backdropFilter: 'blur(10px)', border: '1px solid rgba(0, 245, 255, 0.2)', borderRadius: 8, zIndex: 50, boxShadow: '0 4px 20px rgba(0,0,0,0.5)' }}>
              <div style={{ display: 'flex', alignItems: 'center', background: 'rgba(255,255,255,0.05)', borderRadius: 4, padding: '4px 8px', marginBottom: 12 }}>
                <Icon name="search" size={14} />
                <input
                  style={{ background: 'transparent', border: 'none', color: 'white', padding: 4, outline: 'none', width: '100%', fontSize: 13 }}
                  value={iconQ}
                  onChange={(e) => setIconQ(e.target.value)}
                  placeholder="Buscar ícone…"
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6, maxHeight: 150, overflowY: 'auto', paddingRight: 4 }}>
                {filteredIcons.slice(0, 30).map((ic) => (
                  <button
                    key={ic}
                    style={{ background: nd.icon === ic ? `rgba(0,245,255,0.2)` : 'transparent', border: nd.icon === ic ? `1px solid ${c}` : '1px solid transparent', borderRadius: 4, padding: '6px 0', color: nd.icon === ic ? c : 'var(--muted)', cursor: 'pointer', display: 'flex', justifyContent: 'center', transition: 'all 0.2s' }}
                    onClick={() => { updateNode(nd.id, { icon: ic }); setIconMenuOpen(false); }}
                    title={ic}
                  >
                    <Icon name={ic} size={18} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="syn-section">
          <div className="syn-section-hd">
            <span className="syn-section-lbl">Ações</span>
          </div>
          <div className="syn-actions">
            <button className="syn-act" onClick={() => duplicateNode(nd.id)}>
              <Icon name="content_copy" size={15} /> Duplicar
            </button>
            <button className="syn-act" onClick={() => toggleNodeLock(nd.id)}>
              <Icon name={nd.locked ? 'lock_open' : 'lock'} size={15} />
              {nd.locked ? 'Destravar' : 'Travar'}
            </button>
            <button className="syn-act" onClick={() => enterUniverse(nd.id)}>
              <Icon name="open_in_full" size={15} /> Universo
            </button>
            <button className="syn-act danger" onClick={() => deleteNode(nd.id)}>
              <Icon name="delete" size={15} /> Apagar nó
            </button>
          </div>
        </div>

        <div className="syn-section">
          <div className="syn-section-hd">
            <span className="syn-section-lbl" style={{ color: 'var(--neon)' }}>
              <Icon name="neurology" size={14} /> Inteligência Artificial
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
            <button className="syn-act" style={{ width: '100%', justifyContent: 'center', background: 'rgba(0,245,255,0.05)', border: '1px solid rgba(0,245,255,0.1)' }} onClick={() => suggestAi(nd.content ?? '')} disabled={isSuggesting}>
              <Icon name={isSuggesting ? 'hourglass_empty' : 'auto_awesome'} size={15} /> 
              {isSuggesting ? 'Pensando...' : 'Sugerir Expansões'}
            </button>
            <button className="syn-act" style={{ width: '100%', justifyContent: 'center', background: 'rgba(0,245,255,0.05)', border: '1px solid rgba(0,245,255,0.1)' }} onClick={handleAutoLink} disabled={isLinking}>
              <Icon name={isLinking ? 'hourglass_empty' : 'share'} size={15} /> 
              {isLinking ? 'Procurando...' : 'Encontrar Conexões (Auto-link)'}
            </button>
          </div>
          {suggestion && (
            <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', fontSize: 13, lineHeight: 1.5, color: '#e0e0e0', whiteSpace: 'pre-wrap' }}>
              {suggestion}
            </div>
          )}
          {autoLinkCandidates && (
            <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>Sugestões baseadas no conteúdo:</div>
              {autoLinkCandidates.length === 0 && <div style={{ fontSize: 13, color: '#aaa' }}>Nenhuma sugestão encontrada.</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {autoLinkCandidates.map(c => {
                  const alreadyLinked = links.some(l => (l.source === nd.id && l.target === c.documentId) || (l.target === nd.id && l.source === c.documentId));
                  return (
                    <div key={c.documentId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px', background: 'rgba(0,0,0,0.2)', borderRadius: 6 }}>
                      <div style={{ flex: 1, minWidth: 0, marginRight: 8 }}>
                        <div style={{ fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.title}</div>
                        <div style={{ fontSize: 10, color: 'var(--neon)' }}>Similaridade: {Math.round(c.similarity * 100)}%</div>
                      </div>
                      <button 
                        disabled={alreadyLinked}
                        onClick={() => {
                          useStore.getState().createLink(nd.id, c.documentId, 'synapse');
                          setAutoLinkCandidates(autoLinkCandidates.filter(x => x.documentId !== c.documentId));
                        }}
                        style={{ padding: '4px 8px', borderRadius: 4, background: alreadyLinked ? 'rgba(255,255,255,0.1)' : 'var(--neon)', color: alreadyLinked ? '#888' : '#000', border: 'none', cursor: alreadyLinked ? 'default' : 'pointer', fontSize: 12, fontWeight: 600 }}
                      >
                        {alreadyLinked ? 'Conectado' : 'Conectar'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {nd.type === 'text' ? (
          <div className="syn-section">
            <div className="syn-section-hd">
              <span className="syn-section-lbl">Conteúdo do Texto</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <MicButton
                  size={13}
                  onText={(t) => {
                    const ta = textTextareaRef.current;
                    if (!ta) return;
                    const start = ta.selectionStart ?? ta.value.length;
                    const end = ta.selectionEnd ?? ta.value.length;
                    const v = ta.value.slice(0, start) + ' ' + t + ' ' + ta.value.slice(end);
                    ta.value = v;
                    ta.focus();
                    updateNode(nd.id, { content: v });
                  }}
                />
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--muted-2)' }}>
                  {(nd.content && typeof nd.content === 'string' ? nd.content : '').length} caracteres
                </span>
              </span>
            </div>
            <textarea
              ref={textTextareaRef}
              key={nd.id + '-content-text'}
              className="syn-notes"
              style={{ minHeight: 180, border: '1px solid rgba(0, 255, 136, 0.15)' }}
              defaultValue={nd.content && typeof nd.content === 'string' ? nd.content : ''}
              onChange={(e) => queueContentSave(nd.id, e.target.value)}
              onBlur={(e) => {
                pendingSaveRef.current = { id: nd.id, value: e.target.value };
                flushContentSave();
              }}
              placeholder="Escreva seu texto detalhado aqui…"
            />
          </div>
        ) : nd.type === 'image' ? (
          <div className="syn-section">
            <div className="syn-section-hd">
              <span className="syn-section-lbl">Imagem do Nó</span>
            </div>
            {isUploading ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, gap: 10, background: 'rgba(255,255,255,0.01)', borderRadius: 8, border: '1px dashed rgba(255,204,0,0.2)' }}>
                <Loader2 className="animate-spin" size={24} style={{ color: '#ffcc00' }} />
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>Enviando imagem...</span>
              </div>
            ) : nd.content && typeof nd.content === 'string' && nd.content.trim() ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ width: '100%', borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8 }}>
                  <img
                    src={UUID_REGEX.test(nd.content.trim()) ? `/api/assets/${nd.content.trim()}/serve` : nd.content.trim()}
                    alt={nd.label}
                    style={{ maxWidth: '100%', maxHeight: 180, objectFit: 'contain', borderRadius: 4 }}
                  />
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    padding: '8px 12px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 204, 0, 0.25)',
                    borderRadius: 6,
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    width: '100%',
                    transition: 'all 0.2s',
                  }}
                >
                  <Upload size={13} /> Substituir Imagem
                </button>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: '1px dashed rgba(255, 204, 0, 0.35)',
                  borderRadius: 8,
                  padding: '24px 12px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  cursor: 'pointer',
                  background: 'rgba(255,255,255,0.01)',
                  transition: 'all 0.2s',
                }}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
                <Upload size={18} style={{ color: '#ffcc00' }} />
                <span style={{ fontSize: 12, fontWeight: 500, color: 'white' }}>Clique para enviar imagem</span>
                <span style={{ fontSize: 10, color: 'var(--muted-2)' }}>Formatos suportados: PNG, JPG, WEBP</span>
              </div>
            )}
            {uploadError && (
              <div style={{ fontSize: 11, color: 'var(--error, #ef4444)', marginTop: 6, textAlign: 'center' }}>
                {uploadError}
              </div>
            )}
          </div>
        ) : (
          <div className="syn-section">
            <div className="syn-section-hd">
              <span className="syn-section-lbl">Notas</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--muted-2)' }}>
                {(nd.content ?? '').length} chars
              </span>
            </div>
            <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <textarea
                ref={textareaRef}
                key={nd.id + '-content'}
                className="syn-notes"
                defaultValue={nd.content ?? ''}
                onChange={handleTextareaChange}
                onKeyDown={handleTextareaKeyDown}
                onBlur={(e) => {
                  pendingSaveRef.current = { id: nd.id, value: e.target.value };
                  flushContentSave();
                }}
                placeholder="Anote pensamentos, contextos ou referências sobre este nó… Digite '/' para comandos."
                style={{ paddingRight: 48, minHeight: 180 }}
              />

              {/* Ditado por voz — demais ações ficam no menu '/' */}
              <div
                style={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  display: 'flex',
                  background: 'rgba(10, 10, 15, 0.85)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderRadius: 6,
                  padding: '4px',
                  zIndex: 10,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 10px rgba(0, 245, 255, 0.1)',
                }}
              >
                <MicButton size={14} onText={(t) => insertTextAtCursor(' ' + t + ' ')} />
              </div>

              {/* Popover flutuante do comando / */}
              {slashMenu.visible && getFilteredSlashItems().length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    left: slashMenu.x,
                    top: slashMenu.y,
                    width: 220,
                    maxHeight: 200,
                    overflowY: 'auto',
                    background: 'rgba(10, 10, 15, 0.95)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: 8,
                    padding: 4,
                    zIndex: 100,
                    boxShadow: '0 12px 48px rgba(0,0,0,0.8), 0 0 15px rgba(0, 245, 255, 0.15)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <div style={{ fontSize: 8, textTransform: 'uppercase', color: 'var(--muted-2)', padding: '4px 6px', letterSpacing: 1, fontFamily: 'var(--font-mono)' }}>
                    Comandos Rápidos
                  </div>
                  {getFilteredSlashItems().map((item, idx) => {
                    const isSelected = idx === slashMenu.index;
                    return (
                      <button
                        key={idx}
                        onClick={item.action}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          width: '100%',
                          padding: '6px 8px',
                          background: isSelected ? 'rgba(255, 255, 255, 0.06)' : 'transparent',
                          border: 'none',
                          borderRadius: 6,
                          textAlign: 'left',
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                        }}
                      >
                        <div style={{
                          width: 24,
                          height: 24,
                          borderRadius: 4,
                          background: isSelected ? item.color : 'rgba(255,255,255,0.02)',
                          color: isSelected ? '#0a0a0f' : item.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.15s',
                        }}>
                          <Icon name={item.icon} size={13} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                          <span style={{ fontSize: 12, fontWeight: 500, color: isSelected ? '#ffffff' : 'var(--zinc-300)' }}>
                            {item.label}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <AIConsentModal open={showAiConsent} onClose={() => setShowAiConsent(false)} onSuccess={() => alert('Consentimento concedido! Tente a ação novamente.')} />
    </aside>
  );
}
